import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  extractVersionHistoryResults,
  getAllVersionHistories,
  versionHistoryRowDedupeKey,
} from "../../redux/versionHistorySlice";
import {
  normalizeVersionIdRef,
  uniqueVersionIdRefs,
} from "../../util/versionIdRefUtils";

function collectVersionIds(nodes) {
  const raw = [];
  const traverseNode = (node) => {
    if (node.version_id_ref) raw.push(node.version_id_ref);
    if (node.detail?.version_id_ref) raw.push(node.detail.version_id_ref);
    if (node.steps && Array.isArray(node.steps)) {
      node.steps.forEach((step) => {
        if (step.version_id_ref) raw.push(step.version_id_ref);
        if (step.detail?.version_id_ref) raw.push(step.detail.version_id_ref);
        if (step.substeps && Array.isArray(step.substeps)) {
          step.substeps.forEach((substep) => {
            if (substep.version_id_ref) raw.push(substep.version_id_ref);
            if (substep.detail?.version_id_ref) {
              raw.push(substep.detail.version_id_ref);
            }
            if (substep.children && Array.isArray(substep.children)) {
              substep.children.forEach((child) => {
                if (child.version_id_ref) raw.push(child.version_id_ref);
                if (child.detail?.version_id_ref) {
                  raw.push(child.detail.version_id_ref);
                }
              });
            }
          });
        }
        if (step.children && Array.isArray(step.children)) {
          step.children.forEach((child) => {
            if (child.version_id_ref) raw.push(child.version_id_ref);
            if (child.detail?.version_id_ref) {
              raw.push(child.detail.version_id_ref);
            }
          });
        }
      });
    }
  };
  if (nodes && Array.isArray(nodes)) {
    nodes.forEach((section) => traverseNode(section));
  }
  return uniqueVersionIdRefs(raw);
}

/**
 * Fetches version history for NodeAppBar / view-revision when the popup opens,
 * using procedure revision + part when possible, then batched or per version_id.
 */
export function useVersionHistoryDataFromNodes({
  isOpen,
  revisionId,
  partId,
  allRevisionNodes,
  fallbackVersionId,
  nodesReady = true,
}) {
  const dispatch = useDispatch();
  const { versionHistories: versionHistoriesFromStore } = useSelector(
    (state) => state.versionHistory,
  );

  const [allVersionHistories, setAllVersionHistories] = useState([]);
  const [versionHistoryTableLoading, setVersionHistoryTableLoading] =
    useState(false);

  const mergedVersionHistories = useMemo(
    () =>
      allVersionHistories.length > 0
        ? allVersionHistories
        : versionHistoriesFromStore,
    [allVersionHistories, versionHistoriesFromStore],
  );

  useEffect(() => {
    if (!isOpen) {
      setAllVersionHistories([]);
      setVersionHistoryTableLoading(false);
      return;
    }

    if (!nodesReady) {
      setVersionHistoryTableLoading(true);
      return;
    }

    let versionIds = collectVersionIds(allRevisionNodes || []);
    const fb = normalizeVersionIdRef(fallbackVersionId);
    if (fb && !versionIds.includes(fb)) {
      versionIds.push(fb);
    }
    if (versionIds.length === 0 && fb) {
      versionIds = [fb];
    }
    versionIds = uniqueVersionIdRefs(versionIds);

    if (versionIds.length === 0) {
      setAllVersionHistories([]);
      setVersionHistoryTableLoading(false);
      return;
    }

    setVersionHistoryTableLoading(true);
    let cancelled = false;

    const fetchAllHistories = async () => {
      const applyRows = (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const uniqueHistories = Array.from(
          new Map(
            list.map((item) => [versionHistoryRowDedupeKey(item), item]),
          ).values(),
        );
        if (!cancelled) setAllVersionHistories(uniqueHistories);
      };

      const matchesThisRevision = (row) => {
        const pr = row?.version_content?.procedure_revision_id;
        const prp = row?.parent_version_data?.version_content
          ?.procedure_revision_id;
        if (pr == null && prp == null) return true;
        const id = String(revisionId ?? "");
        return String(pr ?? "") === id || String(prp ?? "") === id;
      };

      try {
        if (revisionId && partId) {
          try {
            const bulk = await dispatch(
              getAllVersionHistories({
                procedureRevisionId: revisionId,
                partRef: partId,
                limit: 200000,
                offset: 0,
              }),
            ).unwrap();
            const rows = extractVersionHistoryResults(bulk);
            if (rows.length > 0) {
              const filtered = rows.filter(matchesThisRevision);
              applyRows(filtered.length ? filtered : rows);
              return;
            }
          } catch (e) {
            console.warn("[useVersionHistoryDataFromNodes] bulk fetch failed", e);
          }
        }

        if (versionIds.length > 1) {
          try {
            const batched = await dispatch(
              getAllVersionHistories({
                versionId: versionIds.join(","),
                limit: 200000,
                offset: 0,
              }),
            ).unwrap();
            const rows = extractVersionHistoryResults(batched);
            if (rows.length > 0) {
              applyRows(rows);
              return;
            }
          } catch (e) {
            console.warn(
              "[useVersionHistoryDataFromNodes] batched version_id failed",
              e,
            );
          }
        }

        const results = await Promise.all(
          versionIds.map((vid) =>
            dispatch(
              getAllVersionHistories({
                versionId: vid,
                limit: 200000,
                offset: 0,
              }),
            ).unwrap(),
          ),
        );
        if (cancelled) return;
        const combinedHistories = results.flatMap(extractVersionHistoryResults);
        applyRows(combinedHistories);
      } catch {
        if (!cancelled) setAllVersionHistories([]);
      } finally {
        if (!cancelled) setVersionHistoryTableLoading(false);
      }
    };

    fetchAllHistories();
    return () => {
      cancelled = true;
      setVersionHistoryTableLoading(false);
    };
  }, [
    isOpen,
    allRevisionNodes,
    fallbackVersionId,
    nodesReady,
    dispatch,
    revisionId,
    partId,
  ]);

  return {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  };
}
